import { useState } from "react";
import {
  COUNTRY_PHONE_CODES,
  validatePhoneNumber,
  formatPhoneNumber,
  type CountryPhoneCode,
} from "@/shared/utils/countryPhoneCodes";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  errors?: string[];
  placeholder?: string;
}

export const PhoneNumberInput = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  errors = [],
  placeholder = "Enter phone number",
}: PhoneNumberInputProps) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneCode>(
    COUNTRY_PHONE_CODES[0] // Default to Kenya
  );
  const detectedCountry = COUNTRY_PHONE_CODES.find((country) =>
    value.startsWith(country.dialCode)
  );
  const effectiveCountry = detectedCountry ?? selectedCountry;
  const isValid = Boolean(value) && validatePhoneNumber(value);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Validate and format
    if (inputValue.trim()) {
      const valid = validatePhoneNumber(inputValue);
      // If valid, format and update parent
      if (valid) {
        const formatted = formatPhoneNumber(inputValue, effectiveCountry.dialCode);
        onChange(formatted);
      } else {
        // Still update parent even if not valid yet (for real-time feedback)
        onChange(inputValue);
      }
    } else {
      onChange("");
    }
  };

  const handleCountryChange = (countryCode: string) => {
    const country = COUNTRY_PHONE_CODES.find((c) => c.code === countryCode);
    if (country) {
      setSelectedCountry(country);

      // If there's already a phone number, reformat it with new country code
      if (value.trim()) {
        const formatted = formatPhoneNumber(value, country.dialCode);
        onChange(formatted);
      }
    }
  };

  const getValidationState = () => {
    if (!value) return "neutral";
    return isValid ? "valid" : "invalid";
  };

  const validationState = getValidationState();

  return (
    <Field>
      <FieldLabel htmlFor={`phone-${label}`}>
        {label} {required && <span className="text-red-500">*</span>}
      </FieldLabel>

      <div className="flex gap-2">
        <Select
          value={effectiveCountry.code}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_PHONE_CODES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span>{country.dialCode}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1">
          <Input
            id={`phone-${label}`}
            type="tel"
            value={value}
            onChange={handlePhoneInputChange}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={20}
            aria-invalid={errors && errors.length > 0 ? true : validationState === "invalid"}
            className={`${
              validationState === "invalid"
                ? "border-red-500 focus-visible:ring-red-500"
                : validationState === "valid"
                  ? "border-green-500 focus-visible:ring-green-500"
                  : ""
            }`}
          />
        </div>

        {value && (
          <div className="flex items-center">
            {isValid ? (
              <span className="text-green-500 text-xl">✓</span>
            ) : (
              <span className="text-red-500 text-xl">✗</span>
            )}
          </div>
        )}
      </div>

      <div className="mt-1 text-xs text-gray-500">
        Format: {effectiveCountry.dialCode} or 0... (Max 20 characters)
      </div>

      {errors && errors.length > 0 && (
        <div className="text-red-500 text-xs mt-1">
          {errors.join(", ")}
        </div>
      )}
    </Field>
  );
};
